import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios, useApiResponseParser, useAuth } from "../";
import { IRole } from "../../interfaces";

export default function useRoles() {
    const { axiosPrivate } = useAxios();
    const { parseSuccessResponseData } = useApiResponseParser();
    const queryClient = useQueryClient();
    const { selectedOrganization } = useAuth();

    function useList(organizationId?: string) {
        const orgId = organizationId ?? selectedOrganization?.Id;

        return useQuery<IRole[]>({
            queryKey: ["roles", orgId ?? "none"],
            queryFn: async () => {
                const response = await axiosPrivate.get("/v1/roles/", {
                    params: { OrganizationId: orgId },
                });
                return parseSuccessResponseData(response);
            },
            enabled: !!orgId,
        });
    }

    function useCreate() {
        return useMutation({
            mutationFn: (parameters: {
                Id?: string;
                Name: string;
                Description?: string;
                OrganizationId: string;
            }) => axiosPrivate.post(`/v1/roles/`, parameters).then(parseSuccessResponseData),
            onSuccess: (_data, variables) => {
                queryClient.invalidateQueries({ queryKey: ["roles", variables.OrganizationId] });
                queryClient.invalidateQueries({ queryKey: ["roles"] });
            },
        });
    }

    function useUpdate() {
        return useMutation({
            mutationFn: (parameters: { Id: string; Name?: string; Description?: string }) =>
                axiosPrivate.put(`/v1/roles/`, parameters).then(parseSuccessResponseData),
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ["roles"] });
            },
        });
    }

    function useDelete() {
        return useMutation({
            mutationFn: (parameters: { Id: string }) =>
                axiosPrivate.delete(`/v1/roles/${parameters.Id}`).then(parseSuccessResponseData),
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ["roles"] });
            },
        });
    }

    return { useList, useCreate, useUpdate, useDelete };
}
